import React from "react";
import { Card } from "@/components/ui/card";
import { TeamAvatar, fmtDateTime } from "@/components/common";

function StandingsTable({ rows, teamId }) {
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[360px] text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr><th className="p-2 text-left">Team</th><th className="p-2">P</th><th className="p-2">W</th><th className="p-2">D</th><th className="p-2">L</th><th className="p-2">Pts</th></tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.team_id} className={`border-t ${row.team_id === teamId ? "bg-primary/5" : ""}`}>
                <td className="p-2">
                  <div className="flex items-center gap-2">
                    <span className="w-4 text-xs text-muted-foreground">{index + 1}.</span>
                    <TeamAvatar team={{ name: row.name, color: row.color }} size={20} />
                    <span className={row.team_id === teamId ? "font-bold" : ""}>{row.name}</span>
                  </div>
                </td>
                <td className="p-2 text-center">{row.played}</td><td className="p-2 text-center">{row.won}</td>
                <td className="p-2 text-center">{row.drawn}</td><td className="p-2 text-center">{row.lost}</td>
                <td className="p-2 text-center font-bold">{row.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function TournamentStandings({ context, teamId }) {
  const groups = context.group_standings || {};
  const grouped = context.tournament.tournament_type === "group_elimination" && Object.keys(groups).length > 0;
  return (
    <section>
      <h2 className="text-label mb-2 text-muted-foreground">Standings</h2>
      {grouped ? (
        <div className="space-y-3">
          {Object.entries(groups).map(([group, rows]) => (
            <div key={group}>
              <h3 className="mb-1 text-sm font-semibold">Group {group}</h3>
              <StandingsTable rows={rows} teamId={teamId} />
            </div>
          ))}
        </div>
      ) : (context.standings || []).length > 0 ? (
        <StandingsTable rows={context.standings} teamId={teamId} />
      ) : <Empty>No standings available yet.</Empty>}
    </section>
  );
}

export function TournamentResults({ context }) {
  const results = context.matches.filter((m) => m.status === "final")
    .sort((a, b) => (b.scheduled_time || "").localeCompare(a.scheduled_time || ""));
  return (
    <section>
      <h2 className="text-label mb-2 text-muted-foreground">Match results</h2>
      {results.length === 0 ? <Empty>No match results yet.</Empty> : (
        <div className="space-y-2">
          {results.map((m) => (
            <Card key={m.id} className="p-3">
              <div className="mb-1 flex justify-between gap-2 text-xs text-muted-foreground">
                <span>{m.round_name}{m.field ? ` · ${m.field}` : ""}</span>
                <span>{fmtDateTime(m.scheduled_time, m.timezone)}</span>
              </div>
              <ResultTeam team={m.team_a} placeholder={m.team_a_placeholder} score={m.score_a} winner={m.winner_id === m.team_a?.id} />
              <ResultTeam team={m.team_b} placeholder={m.team_b_placeholder} score={m.score_b} winner={m.winner_id === m.team_b?.id} />
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

function ResultTeam({ team, placeholder, score, winner }) {
  return (
    <div className={`flex items-center justify-between rounded px-1 py-0.5 ${winner ? "bg-primary/5 font-bold" : ""}`}>
      <div className="flex min-w-0 items-center gap-2">
        {team ? <TeamAvatar team={team} size={22} /> : <div className="h-[22px] w-[22px] rounded bg-muted" />}
        <span className="truncate text-sm">{team?.name || placeholder || "TBD"}</span>
      </div>
      <span className="font-heading font-black">{score ?? "–"}</span>
    </div>
  );
}

const Empty = ({ children }) => <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">{children}</p>;
